import { useEffect, useState, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { RootState } from "../../../state/store";
import { parse } from "date-fns";
import DormantSelection from "./dormantFilterSelection";
import Widget from "../../baseWidget";
import { useWidgetContext } from "../../../helpers/hooks/useWidgetContext";
import TabularList from "./listTable";
import {
  TbSortAscendingNumbers,
  TbSortDescendingNumbers,
} from "react-icons/tb";
import { useMeasure } from "react-use";

interface SortControlProps {
  sortAscending: boolean;
  handleSortModeSelection: () => void;
}

const SortControl = ({ sortAscending, handleSortModeSelection }: SortControlProps) => {
  return (
    <SortButton onClick={handleSortModeSelection}>
      {sortAscending ? (
        <TbSortAscendingNumbers />
      ) : (
        <TbSortDescendingNumbers width={"14px"} />
      )}
    </SortButton>
  );
};

const SortButton = styled.div`
  box-sizing: border-box;
  height: 35px;
  width: 35px;
  border: 2px solid #58585e;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  padding: 2px 8px;
  padding: 0px;
  align-items: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

interface RowData {
  last_order_age: number;
  [key: string]: any;
}

interface DormantOption {
  name: string;
  value: number;
}

const DormantChartWidget = () => {
  const [widgetData, setWidgetData] = useState<any>({});
  const [widgetRef, contentDimensions] = useMeasure<HTMLDivElement>();
  const [selectedOption, setSelectedOption] = useState<DormantOption | null>(null);
  const [widgetHeaderRef, headerDimensions] = useMeasure<HTMLDivElement>();

  const widget = useWidgetContext();
  const { filters } = useSelector((state: RootState) => state);

  const [sortAscending, setSortAscending] = useState(false);
  const [rowLimit, setRowLimit] = useState(50);
  const [isLoading, setIsLoading] = useState(false);

  const selectedOptionRef = useRef<DormantOption | null>(null);

  const dormantEndDate = useMemo(
    () => parse(filters.dates[0], "yyyy-MM-dd", new Date()),
    [filters.dates[0]]
  );

  const dormantMonthOptions: DormantOption[] = [
    {
      name: "in the past 3 Months",
      value: 3,
    },
    {
      name: "in the past 6 Months",
      value: 6,
    },
    {
      name: "in the past 9 Months",
      value: 9,
    },
    {
      name: "in the past 12 Months",
      value: 12,
    },
  ];

  const tableData = useMemo(() => {
    if (widget?.widgetData?.widget_data) {
      return {
        ...widget.widgetData.widget_data,
        data: {
          ...widget.widgetData.widget_data.data,
          rows: widget.widgetData.widget_data.data.rows
            .sort((a: RowData, b: RowData) =>
              sortAscending
                ? a.last_order_age - b.last_order_age
                : b.last_order_age - a.last_order_age
            )
            .filter((_: any, i: number) => i < rowLimit),
        },
      };
    }
    return null;
  }, [sortAscending, rowLimit, widget?.widgetData]);

  // Update widget data and check loading state from request status
  useEffect(() => {
    if (widget?.widgetData?.widget_data) {
      setWidgetData(widget.widgetData);
      if (selectedOptionRef.current) {
        setSelectedOption(selectedOptionRef.current);
      }
      // If we received data, we're no longer loading
      setIsLoading(false);
    }
    
    // Check if widget is in a validating state that indicates loading
    if (widget?.isValidating !== undefined) {
      setIsLoading(widget.isValidating);
    }
  }, [widget?.widgetData, widget?.isValidating]);

  const tableRowData = useMemo(() => {
    if (widgetData?.widget_data) {
      return widgetData.widget_data.data.rows;
    }
    return [];
  }, [JSON.stringify(widgetData)]);

  const handleDormantDaysSelection = (months: number) => {
    if (widget?.getWidgetDataWithFilters) {
      // Set loading before calling the API
      setIsLoading(true); 
      
      widget.getWidgetDataWithFilters([
        { key: "last_order_age", value: months }
      ]);
      
      const selOption = dormantMonthOptions.find((d) => d.value === months) ?? null;
      selectedOptionRef.current = selOption;
      setSelectedOption(selOption);
    }
  };

  const changeSortMode = () => {
    setSortAscending((s) => !s);
  };

  return (
    <Widget ref={widgetRef as any}>
      <div className="widget-header" ref={widgetHeaderRef as any}>
        <Widget.Title>{widgetData?.title}</Widget.Title>
        <Widget.Controls>
          {tableRowData.length > 0 ? (
            <>
              <Widget.ExtendedAReportCta
                additionalFilters={[
                  {
                    key: "last_order_age",
                    value:
                      selectedOption?.value ?? dormantMonthOptions[0].value,
                  },
                ]}
              />
              <DormantSelection
                filterPretext={"Dormant Customers"}
                selectedOptionName={
                  selectedOption?.name ?? dormantMonthOptions[0].name
                }
                dormantDaysOptions={dormantMonthOptions}
                endDate={dormantEndDate}
                handleDormantDaysSelection={handleDormantDaysSelection}
              />
              <SortControl
                sortAscending={sortAscending}
                handleSortModeSelection={changeSortMode}
              />
            </>
          ) : null}
        </Widget.Controls>
      </div>
      <Widget.Content>
        {isLoading ? (
          <LoadingContainer
            style={{
              width: contentDimensions.width,
              height: contentDimensions.height - headerDimensions.height,
            }}>
            <LoadingSpinner />
          </LoadingContainer>
        ) : tableRowData.length > 0 ? (
          <TabularList
            data={tableData}
            width={contentDimensions.width}
            height={contentDimensions.height - headerDimensions.height}
          />
        ) : (
          <Widget.EmptyData
            width={contentDimensions.width}
            height={contentDimensions.height - headerDimensions.height}
          />
        )}
      </Widget.Content>
    </Widget>
  );
};

export default DormantChartWidget;
